/**
 * @fileoverview Token manager for custom authentication.
 * Stores access tokens in memory and refresh tokens in sessionStorage.
 * Replaces the previous keycloak-js integration with a simple token store.
 */

/** @type {string|null} Access token held in memory only (never persisted to storage) */
let _accessToken = null;

/** @constant {string} sessionStorage key for the refresh token */
const REFRESH_TOKEN_KEY = 'cogslog_refresh_token';

/**
 * Get the current access token from memory.
 *
 * @returns {string|null} The JWT access token or null if not set
 */
export function getToken() {
  return _accessToken;
}

/**
 * Get the current refresh token from sessionStorage.
 *
 * @returns {string|null} The refresh token or null if not set
 */
export function getRefreshToken() {
  try {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Store access and refresh tokens from a login or refresh response.
 * Access token is kept in memory only; refresh token goes to sessionStorage.
 *
 * @param {Object} tokens - The token pair
 * @param {string} tokens.access_token - JWT access token
 * @param {string} tokens.refresh_token - Opaque refresh token
 */
export function setTokens({ access_token, refresh_token }) {
  _accessToken = access_token;
  try {
    if (refresh_token) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    }
  } catch {
    // sessionStorage may be unavailable (e.g., private browsing quota exceeded)
  }
}

/**
 * Clear all stored tokens (used on logout or auth failure).
 * Removes access token from memory and refresh token from sessionStorage.
 */
export function clearTokens() {
  _accessToken = null;
  try {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // ignore storage errors
  }
}

/**
 * Check whether an access token is currently held in memory.
 *
 * @returns {boolean} True if an access token exists
 */
export function isTokenSet() {
  return _accessToken !== null;
}

/**
 * Reset all token state (used in test cleanup).
 * Equivalent to clearTokens but exported under a test-friendly name.
 */
export function resetTokens() {
  clearTokens();
}

export default { getToken, getRefreshToken, setTokens, clearTokens, isTokenSet, resetTokens };
